import * as React from 'react';
import { useHistory } from 'react-router';
import { ColorPaletteSelect } from '../components/ColorPaletteSelect';
import {
  useClonePlaceMutation,
  useDeletePlaceEnclosingSvgsMutation,
  useDeletePlaceSymbolSvgsListMutation,
  useGetAccountsQuery,
  useGetCustomersQuery,
  useGetPlaceTagNamesQuery,
  useGetPlaceEnclosingSvgsLazyQuery,
  useGetPlaceSymbolSvgsLazyQuery,
  useGetPlaceLazyQuery,
  useGetPlaceNamesLazyQuery,
  useUpdatePlaceEnclosingSvgsMutation,
  useUpdatePlaceSymbolSvgsListMutation,
  useUpdatePlaceMutation,
  PlaceTagKind
} from '../generated/graphql';
import {
  ConfirmDialog,
  GraphQLErrorDialog,
  SvgNameInputDialog,
} from '../components/Dialogs';
import { Loading } from '../Loading';
import './PlaceCreatePage.css';
import { useValidation } from '../utils/Validation';
import { ValidationRules } from '../utils/ValidationRules';
import { Input, Checkbox } from '../components/Input';
import { Validated } from '../components/Validated';
import { Button } from '../components/Button';
import { UserTagEditor } from '../components/UserTagEditor';
import { ChiefNameInput } from '../components/ChiefNameInput';
import { ReadFilesToStringAsync, splitSvgReadData } from '../utils/ReadFileToString';
import { PageHeader } from '../components/PageHeader';
import { SectionHeader } from '../components/SectionHeader';
import { FormActions } from '../components/FormActions';
import { ToastContext } from '../components/Toast';
import { MultiSelectInput } from '../components/MultiSelectInput';
import { appendWorkerAccounts } from '../components/WorkerBulkAddDialog.logic';

interface PlaceCreatePageProps {
  existingId?: number;
}

interface Account {
  id: number;
  name: string;
  screen_id: string;
}

/** SVGの文字列を、imgのsrcに指定できるdata URLにする */
function toSvgDataUrl(svg: string): string {
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg).replace(/'/g, "%27").replace(/"/g, "%22")}`;
}

/** 物件の詳細（基本情報）のパス。物件IDが取れなかったときは物件一覧のパスを返す */
function getPlacePath(placeId: number | undefined): string {
  if (placeId === undefined) {
    return '/places';
  }
  return `/places/${placeId}`;
}

function getSubmitLabel(existingId: number | undefined): string {
  if (existingId) {
    return '保存';
  }
  return '登録';
}

/** エラーメッセージが出ているときだけ、入力欄からメッセージの要素を参照させる */
function getDescribedBy(validationMessage: string | undefined, messageId: string): string | undefined {
  if (validationMessage) {
    return messageId;
  }
  return undefined;
}

/** 作業者のチェックリストの1項目。同じ名前のユーザーを見分けられるよう、名前にIDを添えて出す */
interface WorkerOption {
  name: string;
  account: Account;
}

/**
 * 作業者のチェックリストの項目。作業者の権限のユーザーを並べ、そのあとに、今の作業者のうち作業者の権限でなくなったユーザーを足す。
 * 足しておくことで、権限が変わったユーザーもチェックを外して作業者から外せる
 */
function buildWorkerOptions(workerAccounts: Account[], registeredAccounts: Account[]): WorkerOption[] {
  const workerAccountIds = workerAccounts.map((a) => a.id);
  const otherRegisteredAccounts = registeredAccounts.filter((a) => workerAccountIds.indexOf(a.id) === -1);
  return [...workerAccounts, ...otherRegisteredAccounts].map((account) => ({
    name: `${account.name} (${account.screen_id})`,
    account,
  }));
}

const RequiredMark: React.FC = function () {
  return (
    <span className="App-label_required" aria-hidden="true">
      *
    </span>
  );
};

export const PlaceCreatePage: React.FC<PlaceCreatePageProps> = function (
  props
) {
  const history = useHistory();
  const toastContext = React.useContext(ToastContext);
  const validation = useValidation(ValidationRules.Place);

  const [copy, setCopy] = React.useState(false);
  const [copySrc, setCopySrc] = React.useState<number | null>(null);
  const [copyCodes, setCopyCodes] = React.useState(false);
  const [copySorts, setCopySorts] = React.useState(false);
  const [copyFaces, setCopyFaces] = React.useState(false);

  const [name, setName] = validation.useState('name', '');
  const [chiefName, setChiefName] = validation.useState('chiefName', '');
  const [customerId, setCustomerId] = validation.useState('customerId', 0);
  const [pictureLayerColor, setPictureLayerColor] = React.useState<string>('#FFFFFF');
  const [showColorPicker, setShowColorPicker] = React.useState(false);
  const [accounts, setAccounts] = React.useState<Account[]>([]);
  const [lv1Accounts, setLv1Accounts] = React.useState<Account[]>([]);
  const [tags, setTags] = React.useState<string[]>([]);

  // SVG管理用のstate
  interface SvgItem {
    id: number;
    svg: string;
  }
  const [existingSvgs, setExistingSvgs] = React.useState<SvgItem[]>([]);
  const [pendingSvgs, setPendingSvgs] = React.useState<string[]>([]);
  // 保存済みの囲み図形のうち、保存したときに削除する図形のID。削除を決めた図形は一覧に出さない
  const [deletedSvgIds, setDeletedSvgIds] = React.useState<number[]>([]);
  const [confirmDeleteSvgId, setConfirmDeleteSvgId] = React.useState<number | null>(null);
  const [confirmDeletePendingIndex, setConfirmDeletePendingIndex] = React.useState<number | null>(null);

  // SymbolSVG管理用のstate
  interface SymbolSvgItem {
    id: number;
    place_id: number | null;
    name: string;
    svg: string;
  }
  const [existingSymbolSvgs, setExistingSymbolSvgs] = React.useState<SymbolSvgItem[]>([]);
  const [pendingSymbolSvgs, setPendingSymbolSvgs] = React.useState<{ name: string; svg: string }[]>([]);
  // 保存済みのシンボル図形のうち、保存したときに削除する図形のID。削除を決めた図形は一覧に出さない
  const [deletedSymbolSvgIds, setDeletedSymbolSvgIds] = React.useState<number[]>([]);
  const [confirmDeleteSymbolSvgId, setConfirmDeleteSymbolSvgId] = React.useState<number | null>(null);
  const [confirmDeletePendingSymbolIndex, setConfirmDeletePendingSymbolIndex] = React.useState<number | null>(null);

  // 名前入力ダイアログ用のstate
  const [showSymbolSvgNameDialog, setShowSymbolSvgNameDialog] = React.useState(false);
  const [tempSymbolSvgItems, setTempSymbolSvgItems] = React.useState<{ fileName: string; svg: string }[]>([]);

  // 名前編集ダイアログ用のstate
  const [editingSymbolSvgId, setEditingSymbolSvgId] = React.useState<number | null>(null);
  const [editingPendingSymbolIndex, setEditingPendingSymbolIndex] = React.useState<number | null>(null);

  // 図形の追加欄ごとの、読み込めなかったファイルの知らせ。追加欄のすぐ下に出す
  const [symbolSvgFileError, setSymbolSvgFileError] = React.useState('');
  const [enclosingSvgFileError, setEnclosingSvgFileError] = React.useState('');

  // 図形として追加するSVGファイルを読み込む。空のファイルや中身がSVGでないファイルは追加せず、そのファイル名を追加欄の知らせに出す。
  // 次にファイルを追加したときは、知らせをそのときの結果で置き換える
  const readSvgFiles = async (files: FileList, setFileError: (message: string) => void) => {
    const { valid, invalidFileNames } = splitSvgReadData(await ReadFilesToStringAsync(files, 'image/svg+xml'));
    if (invalidFileNames.length > 0) {
      setFileError(`「${invalidFileNames.join('」「')}」はSVGの図形として読み込めないため、追加しませんでした`);
    } else {
      setFileError('');
    }
    return valid;
  };

  // 削除確認ダイアログのレンダリングヘルパー
  const renderDeleteConfirmDialog = (
    confirmId: number | null,
    getTarget: () => { svg: string; name?: string } | undefined,
    description: string,
    onConfirm: () => void,
    onCancel: () => void,
  ): React.ReactNode => {
    if (confirmId === null) {
      return null;
    }
    const target = getTarget();
    if (!target) {
      return null;
    }
    const targetName = target.name;
    return (
      <ConfirmDialog
        open={true}
        title="削除の確認"
        message={
          <div>
            <p>{description}</p>
            <div className="PlaceCreatePage-DeletePreview">
              <div className="PlaceCreatePage-DeletePreviewSvg">
                <img src={toSvgDataUrl(target.svg)} alt="削除する図形" />
              </div>
              {(function () {
                if (!targetName) {
                  return null;
                }
                return <div className="PlaceCreatePage-DeletePreviewName">{targetName}</div>;
              })()}
            </div>
          </div>
        }
        yesLabel="削除"
        danger={true}
        onYes={onConfirm}
        onNo={onCancel}
      />
    );
  };

  const customerListResult = useGetCustomersQuery();
  const placeTagNamesResult = useGetPlaceTagNamesQuery({ fetchPolicy: 'network-only' });
  const accountListResult = useGetAccountsQuery({
    onCompleted(data) {
      const lv1Accounts: Account[] = data.getAccounts.filter(
        (a) => a.authority.level < 2
      );
      // 作業者の追加欄は「作業者を選択」の未選択から始める
      setLv1Accounts(lv1Accounts);
    },
  });
  const [getPlace, getPlaceResult] = useGetPlaceLazyQuery({
    onCompleted(data) {
      const place = data.getPlace;
      setName(place.name);
      setChiefName(place.chief_name);
      setCustomerId(place.client_id);
      if (data.getPlace.picture_layer_color) {
        setPictureLayerColor(data.getPlace.picture_layer_color);
      }
      setAccounts(place.authorized_workers);
      setTags(place.tags);
    },
  });
  const [getPlaceNames, getPlaceNamesResult] = useGetPlaceNamesLazyQuery({
    onCompleted(data) {
      if (data.getPlaces.length === 0) {
        return;
      }
      setCopySrc(data.getPlaces[0].id);
    },
  });

  // SVG関連のクエリ・ミューテーション
  const [getPlaceEnclosingSvgs] = useGetPlaceEnclosingSvgsLazyQuery({
    onCompleted(data) {
      setExistingSvgs(data.getPlaceEnclosingSvgs.map(s => ({ id: s.id, svg: s.svg })));
    },
  });
  const [addEnclosingSvgs] = useUpdatePlaceEnclosingSvgsMutation();
  const [deleteEnclosingSvgs] = useDeletePlaceEnclosingSvgsMutation();

  // SymbolSVG関連のクエリ・ミューテーション
  const [getPlaceSymbolSvgs] = useGetPlaceSymbolSvgsLazyQuery({
    onCompleted(data) {
      setExistingSymbolSvgs(data.getPlaceSymbolSvgs.map(s => ({ id: s.id, place_id: s.place_id ?? null, name: s.name, svg: s.svg })));
    },
  });
  const [addSymbolSvgs] = useUpdatePlaceSymbolSvgsListMutation();
  const [deleteSymbolSvgs] = useDeletePlaceSymbolSvgsListMutation();

  const [fetching, setFetching] = React.useState(false);
  const [addPlace] = useUpdatePlaceMutation();
  const [clonePlace] = useClonePlaceMutation();

  if (props.existingId !== undefined) {
    if (!getPlaceResult.called) {
      getPlace({
        variables: {
          placeId: props.existingId,
        },
      });
      getPlaceEnclosingSvgs({
        variables: {
          placeId: props.existingId,
        },
      });
      getPlaceSymbolSvgs({
        variables: {
          placeId: props.existingId,
        },
      });
    }

    if (getPlaceResult.error) {
      return <GraphQLErrorDialog error={getPlaceResult.error} />;
    }

    if (getPlaceResult.loading || !getPlaceResult.data) {
      return <Loading />;
    }
  }

  if (accountListResult.loading || !accountListResult.data) {
    return <Loading />;
  }
  if (customerListResult.loading || !customerListResult.data) {
    return <Loading />;
  }

  // 現在の編集内容に登録されている作業者と、作業者のチェックリストの項目
  const registeredAccountIds = accounts.map((a) => a.id);
  const workerOptions = buildWorkerOptions(lv1Accounts, accounts);

  function isInvalid() {
    if (copy) {
      return validation.isInvalidProperty('name');
    }

    // お客様は保存ボタンを押したときに確かめ、未選択なら入力欄の下にエラーを出す
    return (
      validation.isInvalidProperty('name') ||
      validation.isInvalidProperty('chiefName') ||
      Boolean(validation.get('customerId'))
    );
  }

  // 作業者のチェックを付け外しする。保存すると、チェックが付いている作業者だけが作業者として保存される
  function setWorkersSelected(options: WorkerOption[], selected: boolean) {
    const targetAccounts = options.map((option) => option.account);
    if (selected) {
      setAccounts(appendWorkerAccounts(accounts, targetAccounts));
      return;
    }
    const targetIds = targetAccounts.map((a) => a.id);
    setAccounts(accounts.filter((a) => targetIds.indexOf(a.id) === -1));
  }

  function showSaveErrorToast() {
    if (props.existingId) {
      toastContext.showToast('物件を保存できませんでした。時間をおいて再実行してください。', 'error');
      return;
    }
    toastContext.showToast('物件を登録できませんでした。時間をおいて再実行してください。', 'error');
  }

  async function handleSubmit() {
    // コピーでない場合はお客様未選択をチェック
    if (!copy && customerId === 0) {
      // 未選択の値で検証を走らせ、お客様の入力欄の下にエラーを出す
      setCustomerId(0);
      return;
    }

    // デフォルトのSymbolSVG
    const defaultSymbols = [
      // イ-1
      { name: "イ-1", svg: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1 1"><path d="M1 .707L.707 1H.293L0 .707V.293L.293 0H.707L1 .293z" fill="none" stroke="#000" stroke-width=".02" stroke-linejoin="round"/></svg>` },
      // タイル爆裂
      { name: "タイル爆裂", svg: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1 1"><path d="M1 .707L.707 1H.293L0 .707V.293L.293 0H.707L1 .293zM.193.1H.807M.073.22H.927M.073.78H.927M.193.9H.807" fill="none" stroke="#000" stroke-width=".02" stroke-linejoin="round"/></svg>` },
      // イ-2
      { name: "イ-2", svg: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1 1"><path d="M.5 0L1 .5.5 1 0 .5z" fill="none" stroke="#000" stroke-width=".02"/></svg>` },
      // ロ
      { name: "ロ", svg: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1 1"><path d="M0 0H1V1H0zM.5 0L1 .5.5 1 0 .5z" fill="none" stroke="#000" stroke-width=".02"/></svg>` },
      // タイルはつり
      { name: "タイルはつり", svg: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1 1"><path d="M0 0H1V1H0zM.5 0L1 .5.5 1 0 .5zM.5-.075L1.075.5.5 1.075-.075.5z" fill="none" stroke="#000" stroke-width=".02"/></svg>` },
      // ハ-1
      { name: "ハ-1", svg: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1 1"><path d="M0 0H1V1H0z" fill="none" stroke="#000" stroke-width=".02"/></svg>` },
      // ハ-2
      { name: "ハ-2", svg: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1 1"><path d="M.5.067L1 .933H0z" fill="none" stroke="#000" stroke-width=".02"/></svg>` },
      // ハ-3
      { name: "ハ-3", svg: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1 1"><path d="M.5.933L1 .067H0z" fill="none" stroke="#000" stroke-width=".02"/></svg>` },
      // タイル浮き（一般部）
      { name: "タイル浮き（一般部）", svg: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1 1"><path d="M0 0H1V1H0zM.075.075H.925V.925H.075z" fill="none" stroke="#000" stroke-width=".02"/></svg>` },
      // タイル浮き（指定部）
      { name: "タイル浮き（指定部）", svg: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1 1"><path d="M.5.067L1 .933H0zM.5.217L.87.858H.13z" fill="none" stroke="#000" stroke-width=".02"/></svg>` },
      // タイル浮き（狭幅部）
      { name: "タイル浮き（狭幅部）", svg: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1 1"><path d="M.5.933L1 .067H0zM.5.783L.87.142H.13z" fill="none" stroke="#000" stroke-width=".02"/></svg>` },
      // ホ
      { name: "ホ", svg: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1 1"><circle cx=".5" cy=".5" r=".5" fill="none" stroke="#000" stroke-width=".02"/></svg>` }
    ];

    if (copy) {
      if (!copySrc) {
        throw new Error('copySrc is null');
      }

      setFetching(true);

      try {
        // Placeをコピー（SVGもバックエンド側でコピーされる）
        const result = await clonePlace({
          variables: {
            input: {
              place_id: copySrc,
              name,
              codes: copyCodes,
              sorts: copySorts,
              faces: copyFaces,
            },
          },
        });

        setFetching(false);
        toastContext.showToast('物件を登録しました', 'success');
        history.push(getPlacePath(result.data?.clonePlace.id));
      } catch (e) {
        setFetching(false);
        showSaveErrorToast();
      }
      return;
    }

    setFetching(true);

    try {
      // 削除対象のSVGを削除
      if (deletedSvgIds.length > 0) {
        await deleteEnclosingSvgs({ variables: { ids: deletedSvgIds } });
      }

      // 新規SVGを追加
      if (props.existingId && pendingSvgs.length > 0) {
        await addEnclosingSvgs({
          variables: {
            inputs: pendingSvgs.map(svg => ({
              place_id: props.existingId!,
              svg,
            })),
          },
        });
      }

      // 削除対象のSymbolSVGを削除
      if (deletedSymbolSvgIds.length > 0) {
        await deleteSymbolSvgs({ variables: { ids: deletedSymbolSvgIds } });
      }

      // 新規SymbolSVGを追加
      if (props.existingId && pendingSymbolSvgs.length > 0) {
        await addSymbolSvgs({
          variables: {
            inputs: pendingSymbolSvgs.map(svgItem => ({
              place_id: props.existingId!,
              name: svgItem.name,
              svg: svgItem.svg,
            })),
          },
        });
      }

      const result = await addPlace({
        variables: {
          input: {
            id: props.existingId,
            name: name,
            chief_name: chiefName,
            customer_id: customerId,
            picture_layer_color: pictureLayerColor,
            authorized_worker_ids: accounts.map((a) => a.id),
            tags: tags
          },
        },
      });

      // 新規作成の場合はデフォルトのSVGを追加
      if (!props.existingId && result.data?.updatePlace.id) {
        const newPlaceId = result.data.updatePlace.id;
        await addEnclosingSvgs({
          variables: {
            inputs: defaultSymbols.map(defaultSymbol => ({ place_id: newPlaceId, svg: defaultSymbol.svg }))
          },
        });
      }

      setFetching(false);
      if (props.existingId) {
        toastContext.showToast('物件を保存しました', 'success');
        history.push(getPlacePath(props.existingId));
        return;
      }
      toastContext.showToast('物件を登録しました', 'success');
      history.push(getPlacePath(result.data?.updatePlace.id));
    } catch (e) {
      setFetching(false);
      showSaveErrorToast();
    }
  }

  function handleCancel() {
    if (props.existingId) {
      history.push(getPlacePath(props.existingId));
      return;
    }
    history.push('/places');
  }

  return (
    <div className="PlaceCreatePage-container">
      <Loading
        open={(getPlaceNamesResult.called && getPlaceNamesResult.loading) || fetching}
      />
      {(function () {
        if (props.existingId) {
          return <SectionHeader title="物件情報の編集" />;
        }
        return (
          <PageHeader
            title="物件の登録"
            breadcrumbs={[{ label: '物件一覧', to: '/places' }, { label: '物件の登録' }]}
          />
        );
      })()}
      {(function () {
        if (props.existingId) {
          return;
        }

        return (
          <div>
            <label className="PlaceCreatePage-copy_label">
              <Checkbox
                value={copy}
                onChange={(v) => {
                  if (!getPlaceNamesResult.called) {
                    getPlaceNames();
                  }
                  setCopy(v);
                }}
              />
              物件情報をコピーする
            </label>
          </div>
        );
      })()}
      <div className="PlaceCreatePage-form">
        <div className="App-input_container PlaceCreatePage-input_container">
          <label className="App-label" htmlFor="PlaceCreatePage-name">
            物件名
            <RequiredMark />
          </label>
          <Input
            id="PlaceCreatePage-name"
            className="PlaceCreatePage-place_name_input"
            type="string"
            required={true}
            value={name}
            validationMessage={validation.get('name')}
            onChange={(event) => {
              const v = event.target.value;
              setName(() => v);
            }}
          />
        </div>
        {(function () {
          if (copy) {
            if (
              getPlaceNamesResult.loading ||
              !getPlaceNamesResult.data ||
              !copySrc
            ) {
              return;
            }

            if (getPlaceNamesResult.data.getPlaces.length === 0) {
              return <p>物件が登録されていません</p>;
            }

            // 図面を引き継ぐと、サーバーは補修コードと補修分類も必ず引き継ぐ（マーカーが両方を参照するため）。
            // その関係が見て分かるよう、図面を選んでいる間は2つをチェック済みで押せない表示にする。送る値は変えない
            const codesShownChecked = copyCodes || copyFaces;
            const sortsShownChecked = copySorts || copyFaces;

            return (
              <div>
                <div className="PlaceCreatePage-input_container">
                  <label className="App-label" htmlFor="PlaceCreatePage-copy_source">
                    コピー元の物件
                  </label>
                  <select
                    id="PlaceCreatePage-copy_source"
                    value={copySrc}
                    onChange={(event) =>
                      setCopySrc(parseInt(event.target.value))
                    }
                  >
                    {getPlaceNamesResult.data.getPlaces.map((p) => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </select>
                </div>
                {/* コピー元を選んだだけですべてを引き継ぐわけではないことが分かるよう、追加で選ぶ内容はコピー元の物件の欄の下へ字下げして置く */}
                <div className="PlaceCreatePage-copy_options">
                  <span className="App-label">追加で引き継ぐ内容</span>
                  <div className="PlaceCreatePage-copy_option">
                    <label className="PlaceCreatePage-copy_label">
                      <Checkbox
                        value={codesShownChecked}
                        disabled={copyFaces}
                        onChange={(v) => setCopyCodes(v)}
                      />
                      補修コード
                    </label>
                  </div>
                  <div className="PlaceCreatePage-copy_option">
                    <label className="PlaceCreatePage-copy_label">
                      <Checkbox
                        value={sortsShownChecked}
                        disabled={copyFaces}
                        onChange={(v) => setCopySorts(v)}
                      />
                      補修分類
                    </label>
                  </div>
                  <div className="PlaceCreatePage-copy_option">
                    <label className="PlaceCreatePage-copy_label">
                      <Checkbox
                        value={copyFaces}
                        onChange={(v) => setCopyFaces(v)}
                      />
                      図面
                    </label>
                  </div>
                  <p className="PlaceCreatePage-copy_note">補修コードと補修分類の両方を選ぶと、分類×コードも引き継ぎます</p>
                </div>
              </div>
            );
          }

          return (
            <div>
              <div className="App-input_container PlaceCreatePage-input_container">
                <label className="App-label" htmlFor="PlaceCreatePage-chief_name">
                  責任者名
                  <RequiredMark />
                </label>
                <ChiefNameInput
                  id="PlaceCreatePage-chief_name"
                  className="PlaceCreatePage-place_chief_name_input"
                  value={chiefName}
                  existingChiefNames={(placeTagNamesResult.data?.getPlaceTags ?? []).filter(t => t.kind === PlaceTagKind.Chief).map(t => t.name)}
                  validationMessage={validation.get('chiefName')}
                  onChange={(name) => setChiefName(() => name)}
                />
              </div>
              <div className="App-input_container PlaceCreatePage-input_container">
                <label className="App-label" htmlFor="PlaceCreatePage-client">
                  お客様
                  <RequiredMark />
                </label>
                <Validated
                  message={validation.get('customerId')}
                  messageId="PlaceCreatePage-client-message"
                >
                  <select
                    id="PlaceCreatePage-client"
                    className="PlaceCreatePage-client_select"
                    aria-required={true}
                    aria-invalid={Boolean(validation.get('customerId'))}
                    aria-describedby={getDescribedBy(validation.get('customerId'), 'PlaceCreatePage-client-message')}
                    value={customerId}
                    onChange={(event) => {
                      const v = parseInt(event.target.value);
                      setCustomerId(() => v);
                    }}
                  >
                    {customerId === 0 && <option value="0">-</option>}
                    {customerListResult.data.getCustomers.map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </Validated>
              </div>
              <div className='App-input_container PlaceCreatePage-input_container'>
                <label className="App-label" htmlFor="PlaceCreatePage-picture_layer_color">
                  写真レイヤの画層色
                </label>
                <button
                  id="PlaceCreatePage-picture_layer_color"
                  type="button"
                  className="PlaceCreatePage-color_button"
                  aria-expanded={showColorPicker}
                  title="クリックで色の選択を開閉"
                  onClick={() => setShowColorPicker(!showColorPicker)}
                >
                  <span
                    className="PlaceCreatePage-color_swatch"
                    style={{ backgroundColor: pictureLayerColor }}
                  ></span>
                  <span className="PlaceCreatePage-color_hex">{pictureLayerColor}</span>
                </button>
                {(function () {
                  if (!showColorPicker) {
                    return null;
                  }
                  // 色は定義済みのパレットから選び、パレットにない色は「その他の色…」で指定する
                  return (
                    <div className="PlaceCreatePage-color_picker">
                      <ColorPaletteSelect
                        color={pictureLayerColor}
                        onChange={setPictureLayerColor}
                      />
                    </div>
                  );
                })()}
              </div>
              <div className="App-input_container PlaceCreatePage-input_container">
                <span className="App-label">作業者</span>
                {/* 選択欄の形の欄を押すと開く、検索欄つきのチェックリストで作業者を付け外しする。
                    チェックを付けたユーザーが欄の中に × 付きの札で並び、保存するとその人たちが作業者になる */}
                <MultiSelectInput
                  label="作業者"
                  placeholder="作業者を選択"
                  items={workerOptions}
                  getKey={(option) => `${option.account.id}`}
                  isSelected={(option) => registeredAccountIds.indexOf(option.account.id) !== -1}
                  onSetSelected={setWorkersSelected}
                />
                {(function () {
                  if (workerOptions.length > 0) {
                    return null;
                  }
                  return <p className="PlaceCreatePage-worker_note">作業者の権限のユーザーが登録されていません</p>;
                })()}
              </div>
              {(function () {
                if (!getPlaceResult.data) {
                  return;
                }

                return (
                  <div className="App-input_container PlaceCreatePage-input_container">
                    <label className="App-label">タグ</label>
                    <UserTagEditor
                      tags={tags}
                      onChange={setTags}
                      existingTagNames={(placeTagNamesResult.data?.getPlaceTags ?? []).filter(t => t.kind === PlaceTagKind.User).map(t => t.name)}
                    />
                  </div>
                );
              })()}
              {(function () {
                if (!getPlaceResult.data) {
                  return;
                }

                // 表示用のSymbolSVG一覧（既存のもの + まだ保存していないもの）。削除を決めた図形は一覧に出さない
                // デフォルトシンボル(place_id === null)を先頭に、それ以外を後ろに表示
                const displayExistingSymbolSvgs = existingSymbolSvgs
                  .filter(s => !deletedSymbolSvgIds.includes(s.id))
                  .sort((a, b) => {
                    if (a.place_id === null && b.place_id !== null) {
                      return -1;
                    }
                    if (a.place_id !== null && b.place_id === null) {
                      return 1;
                    }
                    return 0;
                  });

                return (
                  <div className="App-input_container PlaceCreatePage-input_container">
                    <label className="App-label">
                      追加シンボル図形
                    </label>
                    <label>.svgファイルをアップロードすることで、物件固有のシンボル図形を追加することができます。</label>
                    <div className='PlaceCreatePage-SvgList'>
                      <label className='PlaceCreatePage-SvgAddButton'
                        onDragOver={(e) => {
                          // これが無いと drop が発火しないことが多い
                          e.preventDefault();
                          e.stopPropagation();

                          // "copy" にすると「コピーでドロップ」表示になりやすい
                          e.dataTransfer.dropEffect = "copy";
                        }}
                        onDrop={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          const files = e.dataTransfer.files;
                          if (files) {
                            readSvgFiles(files, setSymbolSvgFileError).then(valid => {
                              const validData = valid.map(data => ({ fileName: data.fileName, svg: data.content }));
                              if (validData.length > 0) {
                                setTempSymbolSvgItems(validData);
                                setShowSymbolSvgNameDialog(true);
                              }
                            });
                          }
                        }}>
                        <input
                          className='PlaceCreatePage-SvgFileInput'
                          type="file"
                          accept='.svg'
                          multiple
                          onChange={(e) => {
                            const files = e.target.files;
                            if (files) {
                              readSvgFiles(files, setSymbolSvgFileError).then(valid => {
                                const validData = valid.map(data => ({ fileName: data.fileName, svg: data.content }));
                                if (validData.length > 0) {
                                  setTempSymbolSvgItems(validData);
                                  setShowSymbolSvgNameDialog(true);
                                }
                              });
                            }
                            e.target.value = '';
                          }}
                        />
                        <div className='PlaceCreatePage-SvgAddButtonLabelContainer'>
                          <div className='PlaceCreatePage-SvgAddButtonLabel PlaceCreatePage-SvgAddButtonLabel_main'>ここにsvgファイルをドラッグ＆ドロップ</div>
                          <div className='PlaceCreatePage-SvgAddButtonLabel PlaceCreatePage-SvgAddButtonLabel_sub'>クリックでファイルを選択</div>
                        </div>
                      </label>
                      {symbolSvgFileError !== '' && (
                        <p className='PlaceCreatePage-SvgFileError' role='alert'>{symbolSvgFileError}</p>
                      )}
                      {(displayExistingSymbolSvgs.length > 0 || pendingSymbolSvgs.length > 0) && (
                        <div className='PlaceCreatePage-SvgListContainer'>
                          {/* 既存のSymbolSVG */}
                          {displayExistingSymbolSvgs.map(svgItem => {
                            const isDefault = svgItem.place_id === null;
                            let containerClassName = 'PlaceCreatePage-SvgContainer';
                            if (isDefault) {
                              containerClassName += ' PlaceCreatePage-SvgDefault';
                            }
                            return (
                              <div key={svgItem.id} className='PlaceCreatePage-SvgItemWrapper'>
                                <div className={containerClassName}>
                                  <img src={toSvgDataUrl(svgItem.svg)} alt={`シンボル図形「${svgItem.name}」`} />
                                </div>
                                <div className='PlaceCreatePage-SvgName' title={svgItem.name}>{svgItem.name}</div>
                                {(function () {
                                  if (isDefault) {
                                    return (
                                      <div
                                        className='PlaceCreatePage-SvgNote'
                                        title='デフォルトシンボルは名前の変更・削除ができません'
                                      >
                                        デフォルト
                                      </div>
                                    );
                                  }
                                  return (
                                    <div className='PlaceCreatePage-SvgActions'>
                                      <Button
                                        type="button"
                                        color="white"
                                        mini={true}
                                        className='PlaceCreatePage-SvgActionButton'
                                        onClick={() => setEditingSymbolSvgId(svgItem.id)}
                                      >
                                        名前変更
                                      </Button>
                                      <Button
                                        type="button"
                                        color="red"
                                        mini={true}
                                        className='PlaceCreatePage-SvgActionButton'
                                        onClick={() => setConfirmDeleteSymbolSvgId(svgItem.id)}
                                      >
                                        削除
                                      </Button>
                                    </div>
                                  );
                                })()}
                              </div>
                            );
                          })}
                          {/* 新規追加予定のSymbolSVG */}
                          {pendingSymbolSvgs.map((svgItem, index) => {
                            return (
                              <div key={`pending-symbol-${index}`} className='PlaceCreatePage-SvgItemWrapper'>
                                <div className='PlaceCreatePage-SvgContainer PlaceCreatePage-SvgPending'>
                                  <img src={toSvgDataUrl(svgItem.svg)} alt={`シンボル図形「${svgItem.name}」`} />
                                </div>
                                <div className='PlaceCreatePage-SvgName' title={svgItem.name}>{svgItem.name}</div>
                                <div className='PlaceCreatePage-SvgActions'>
                                  <Button
                                    type="button"
                                    color="white"
                                    mini={true}
                                    className='PlaceCreatePage-SvgActionButton'
                                    onClick={() => setEditingPendingSymbolIndex(index)}
                                  >
                                    名前変更
                                  </Button>
                                  <Button
                                    type="button"
                                    color="red"
                                    mini={true}
                                    className='PlaceCreatePage-SvgActionButton'
                                    onClick={() => setConfirmDeletePendingSymbolIndex(index)}
                                  >
                                    削除
                                  </Button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}
              {(function () {
                if (!getPlaceResult.data) {
                  return;
                }

                // 表示用のSVG一覧（既存のもの + まだ保存していないもの）。削除を決めた図形は一覧に出さない
                const displayExistingSvgs = existingSvgs.filter(s => !deletedSvgIds.includes(s.id));

                return (
                  <div className="App-input_container PlaceCreatePage-input_container">
                    <label className="App-label">
                      追加囲み図形
                    </label>
                    <label>.svgファイルをアップロードすることで、物件固有の囲み図形を追加することができます。</label>
                    <div className='PlaceCreatePage-SvgList'>
                      <label className='PlaceCreatePage-SvgAddButton'
                        onDragOver={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          e.dataTransfer.dropEffect = "copy";
                        }}
                        onDrop={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          const files = e.dataTransfer.files;
                          if (files) {
                            readSvgFiles(files, setEnclosingSvgFileError).then(valid => {
                              if (valid.length > 0) {
                                setPendingSvgs([...pendingSvgs, ...valid.map(data => data.content)]);
                              }
                            });
                          }
                        }}>
                        <input
                          className='PlaceCreatePage-SvgFileInput'
                          type="file"
                          accept='.svg'
                          multiple
                          onChange={(e) => {
                            const files = e.target.files;
                            if (files) {
                              readSvgFiles(files, setEnclosingSvgFileError).then(valid => {
                                if (valid.length > 0) {
                                  setPendingSvgs([...pendingSvgs, ...valid.map(data => data.content)]);
                                }
                              });
                            }
                            e.target.value = '';
                          }}
                        />
                        <div className='PlaceCreatePage-SvgAddButtonLabelContainer'>
                          <div className='PlaceCreatePage-SvgAddButtonLabel PlaceCreatePage-SvgAddButtonLabel_main'>ここにsvgファイルをドラッグ＆ドロップ</div>
                          <div className='PlaceCreatePage-SvgAddButtonLabel PlaceCreatePage-SvgAddButtonLabel_sub'>クリックでファイルを選択</div>
                        </div>
                      </label>
                      {enclosingSvgFileError !== '' && (
                        <p className='PlaceCreatePage-SvgFileError' role='alert'>{enclosingSvgFileError}</p>
                      )}
                      {(displayExistingSvgs.length > 0 || pendingSvgs.length > 0) && (
                        <div className='PlaceCreatePage-SvgListContainer'>
                          {/* 既存のSVG */}
                          {displayExistingSvgs.map(svgItem => {
                            return (
                              <div key={svgItem.id} className='PlaceCreatePage-SvgItemWrapper'>
                                <div className='PlaceCreatePage-SvgContainer'>
                                  <img src={toSvgDataUrl(svgItem.svg)} alt="囲み図形" />
                                </div>
                                <div className='PlaceCreatePage-SvgActions'>
                                  <Button
                                    type="button"
                                    color="red"
                                    mini={true}
                                    className='PlaceCreatePage-SvgActionButton'
                                    onClick={() => setConfirmDeleteSvgId(svgItem.id)}
                                  >
                                    削除
                                  </Button>
                                </div>
                              </div>
                            );
                          })}
                          {/* 新規追加予定のSVG */}
                          {pendingSvgs.map((svg, index) => {
                            return (
                              <div key={`pending-${index}`} className='PlaceCreatePage-SvgItemWrapper'>
                                <div className='PlaceCreatePage-SvgContainer PlaceCreatePage-SvgPending'>
                                  <img src={toSvgDataUrl(svg)} alt="囲み図形" />
                                </div>
                                <div className='PlaceCreatePage-SvgActions'>
                                  <Button
                                    type="button"
                                    color="red"
                                    mini={true}
                                    className='PlaceCreatePage-SvgActionButton'
                                    onClick={() => setConfirmDeletePendingIndex(index)}
                                  >
                                    削除
                                  </Button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}
            </div>
          );
        })()}
        <FormActions
          submitLabel={getSubmitLabel(props.existingId)}
          submitDisabled={isInvalid()}
          onSubmit={() => handleSubmit()}
          onCancel={() => handleCancel()}
        />
      </div>

      {/* シンボルSVG名前入力ダイアログ */}
      <SvgNameInputDialog
        open={showSymbolSvgNameDialog}
        svgItems={tempSymbolSvgItems}
        onConfirm={(items) => {
          setPendingSymbolSvgs([...pendingSymbolSvgs, ...items]);
          setShowSymbolSvgNameDialog(false);
          setTempSymbolSvgItems([]);
        }}
        onCancel={() => {
          setShowSymbolSvgNameDialog(false);
          setTempSymbolSvgItems([]);
        }}
      />

      {/* 図形の削除の確認。保存済みの図形は削除するIDに加えて一覧から外し、保存したときに削除する。まだ保存していない図形はその場で一覧から外す */}
      {renderDeleteConfirmDialog(
        confirmDeleteSymbolSvgId,
        () => existingSymbolSvgs.find(s => s.id === confirmDeleteSymbolSvgId),
        'このシンボル図形を削除しますか？',
        () => {
          setDeletedSymbolSvgIds([...deletedSymbolSvgIds, confirmDeleteSymbolSvgId!]);
          setConfirmDeleteSymbolSvgId(null);
        },
        () => setConfirmDeleteSymbolSvgId(null),
      )}
      {renderDeleteConfirmDialog(
        confirmDeletePendingSymbolIndex,
        () => pendingSymbolSvgs[confirmDeletePendingSymbolIndex!],
        'このシンボル図形を削除しますか？',
        () => {
          setPendingSymbolSvgs(pendingSymbolSvgs.filter((_, i) => i !== confirmDeletePendingSymbolIndex));
          setConfirmDeletePendingSymbolIndex(null);
        },
        () => setConfirmDeletePendingSymbolIndex(null),
      )}
      {renderDeleteConfirmDialog(
        confirmDeleteSvgId,
        () => existingSvgs.find(s => s.id === confirmDeleteSvgId),
        'この囲み図形を削除しますか？',
        () => {
          setDeletedSvgIds([...deletedSvgIds, confirmDeleteSvgId!]);
          setConfirmDeleteSvgId(null);
        },
        () => setConfirmDeleteSvgId(null),
      )}
      {renderDeleteConfirmDialog(
        confirmDeletePendingIndex,
        () => {
          // 図形の中身ではなく、一覧の何番目かで確かめる（中身が空の文字列でも削除できるようにする）
          if (confirmDeletePendingIndex === null || confirmDeletePendingIndex >= pendingSvgs.length) {
            return undefined;
          }
          return { svg: pendingSvgs[confirmDeletePendingIndex] };
        },
        'この囲み図形を削除しますか？',
        () => {
          setPendingSvgs(pendingSvgs.filter((_, i) => i !== confirmDeletePendingIndex));
          setConfirmDeletePendingIndex(null);
        },
        () => setConfirmDeletePendingIndex(null),
      )}

      {/* 既存SymbolSVG名前編集ダイアログ */}
      <SvgNameInputDialog
        open={editingSymbolSvgId !== null}
        editMode={true}
        svgItems={(() => {
          const targetSvg = existingSymbolSvgs.find(s => s.id === editingSymbolSvgId);
          return targetSvg ? [{ fileName: targetSvg.name, svg: targetSvg.svg }] : [];
        })()}
        onConfirm={(items) => {
          if (items.length > 0) {
            setExistingSymbolSvgs(existingSymbolSvgs.map(s =>
              s.id === editingSymbolSvgId ? { ...s, name: items[0].name } : s
            ));
          }
          setEditingSymbolSvgId(null);
        }}
        onCancel={() => setEditingSymbolSvgId(null)}
      />

      {/* 新規追加予定SymbolSVG名前編集ダイアログ */}
      <SvgNameInputDialog
        open={editingPendingSymbolIndex !== null}
        editMode={true}
        svgItems={(() => {
          const targetSvg = editingPendingSymbolIndex !== null ? pendingSymbolSvgs[editingPendingSymbolIndex] : undefined;
          return targetSvg ? [{ fileName: targetSvg.name, svg: targetSvg.svg }] : [];
        })()}
        onConfirm={(items) => {
          if (items.length > 0) {
            setPendingSymbolSvgs(pendingSymbolSvgs.map((s, i) =>
              i === editingPendingSymbolIndex ? { ...s, name: items[0].name } : s
            ));
          }
          setEditingPendingSymbolIndex(null);
        }}
        onCancel={() => setEditingPendingSymbolIndex(null)}
      />
    </div>
  );
};
